import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CardContent,
  Grid,
  TextField,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  MenuItem,
  Typography,
  Divider,
  Box,
  Card,
  CardMedia,
} from '@mui/material';
import { StyledCard, ActionButtons } from './ProductEditCard.styles';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { 
  getCategories, 
  getManufacturerList,
  getExtractedImages,
  selectProductImages,
  extractImagesForProduct,
  extractImagesForRegularProduct,
  selectRegularProductImages,
  runAIInferenceByProductDetails,
  uploadCustomImage,
  getVendors
} from '../../api/api';
import { useToast } from '../../components/ToastProvider/ToastProvider';

const CONDITION_OPTIONS = [
  'New',
  'F/S',
  'NOB',
  'Refurbished',
  'Refurbished - Not Audited',
  'Factory Refurbished'
];

const ProductEditCard = ({ product, onApprove, onDelete, onChange }) => {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  
  // State for categories, manufacturers, and vendors
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingMfrs, setLoadingMfrs] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);
  
  // State for AI and image functionality
  const [aiLoading, setAiLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Image state management
  const [currentImages, setCurrentImages] = useState([]); // Current S3 images (green, pre-selected)
  const [extractedImages, setExtractedImages] = useState([]); // Extracted candidates (selectable)
  const [removedImageIds, setRemovedImageIds] = useState([]); // Images marked for deletion
  const [hasCustomUploaded, setHasCustomUploaded] = useState(false); // Track if custom images were uploaded

  // Load categories, manufacturers, and vendors on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [catsList, mfrsList, vendorsList] = await Promise.all([
          getCategories("skip=0&limit=200"),
          getManufacturerList(),
          getVendors()
        ]);
        setCategories(Array.isArray(catsList) ? catsList : []);
        setManufacturers(Array.isArray(mfrsList) ? mfrsList : []);
        setVendors(Array.isArray(vendorsList) ? vendorsList : []);
      } catch (error) {
        console.error('Failed to load categories/manufacturers/vendors:', error);
        setCategories([]);
        setManufacturers([]);
        setVendors([]);
      } finally {
        setLoadingCats(false);
        setLoadingMfrs(false);
        setLoadingVendors(false);
      }
    };
    loadData();
  }, []);

  // Load current images when product changes
  useEffect(() => {
    if (product?.id) {
      console.log('ProductEditCard - Product data:', {
        id: product.id,
        image_urls: product.image_urls,
        image_url: product.image_url,
        brand: product.brand,
        category: product.category
      });
      loadCurrentImages();
    }
  }, [product?.id]);

  const loadCurrentImages = async () => {
    if (!product?.id) return;
    
    try {
      // Load current product images from image_urls field
      if (product.image_urls && product.image_urls.length > 0) {
        const currentImages = product.image_urls.map((url, index) => ({
          id: `current-${index}`,
          url: url,
          title: `Product Image ${index + 1}`,
          source: 'current'
        }));
        setCurrentImages(currentImages);
        console.log(`Loaded ${currentImages.length} current images for product ${product.id}`);
      } else {
        setCurrentImages([]);
        console.log(`No current images for product ${product.id}`);
      }
    } catch (error) {
      console.log(`Error loading current images for product ${product.id}:`, error);
      setCurrentImages([]);
    }
  };

  const numOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const intOrZero = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  // Action handlers
  const handleRunAI = async () => {
    if (!product?.id) return;
    setAiLoading(true);
    try {
      console.log('Running AI for product:', product.id);
      
      // Use product details for AI inference (for regular products)
      const productDetails = {
        sku: product.sku,
        description: product.description,
        brand: product.brand,
        category: product.category,
        concatenate_code: product.concatenate_code
      };
      
      const response = await runAIInferenceByProductDetails(productDetails);
      
      console.log('AI inference response:', response);
      
      if (response.success) {
        console.log('AI data received:', response.data);
        showToast('AI inference completed successfully - existing data overridden', 'success');
        // OVERRIDE: Update product data with AI results
        if (response.data) {
          console.log('Updating fields with:', response.data);
          onChange?.('weight', response.data.weight);
          onChange?.('dimensions', response.data.dimensions);
          onChange?.('ai_low', response.data.ai_low);
          onChange?.('ai_high', response.data.ai_high);
        }
      } else {
        showToast('AI inference failed', 'error');
      }
    } catch (error) {
      console.error('AI inference failed:', error);
      showToast('AI inference failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const handleExtractImages = async () => {
    if (!product?.id) return;
    setImageLoading(true);
    try {
      console.log('Extracting images for regular product:', product.id);
      
      // Use the correct API for regular products
      const response = await extractImagesForRegularProduct(product.id);
      
      if (response.success && response.images) {
        // Convert to selectable format
        const candidates = response.images.map(img => ({
          id: img.id || img.url,
          url: img.url,
          title: img.title || 'Extracted Image',
          source: 'extracted',
          selected: false // Start unselected (blue)
        }));
        
        setExtractedImages(candidates);
        showToast(`Found ${candidates.length} candidate images. Select the ones you want to keep.`, 'info');
      } else {
        showToast(response.message || 'Image extraction failed', 'error');
      }
    } catch (error) {
      console.error('Image extraction failed:', error);
      showToast('Image extraction failed: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setImageLoading(false);
    }
  };

  const handleUploadCustomImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Check max 3 images limit - only count files being uploaded
    if (files.length > 3) {
      showToast('Maximum 3 images can be uploaded at once. Please select fewer images.', 'warning');
      return;
    }
    
    // Check total limit after upload
    const currentSelectedCount = currentImages.filter(img => !removedImageIds.includes(img.id)).length;
    const extractedSelectedCount = extractedImages.filter(img => img.selected).length;
    const totalSelected = currentSelectedCount + extractedSelectedCount;
    
    // If we're replacing all images, allow up to 3
    // Otherwise check if adding new ones exceeds limit
    const willReplaceAll = currentSelectedCount === 0 && extractedSelectedCount === 0;
    
    if (!willReplaceAll && totalSelected + files.length > 3) {
      showToast('Maximum 3 images allowed total. Please remove some images first.', 'warning');
      return;
    }
    
    setUploadLoading(true);
    const uploadedUrls = [];
    
    try {
      // Upload files sequentially (limit to 3)
      const filesToUpload = files.slice(0, 3);
      for (const file of filesToUpload) {
        const response = await uploadCustomImage(product.id, file);
        if (response.image_url) {
          uploadedUrls.push(response.image_url);
        }
      }
      
      if (uploadedUrls.length > 0) {
        // Add uploaded images to current images (don't replace if limit allows)
        const newCurrentImages = uploadedUrls.map((url, index) => ({
          id: `uploaded-${Date.now()}-${index}`,
          url,
          title: `Custom Image ${index + 1}`,
          source: 'custom'
        }));
        
        // If we have space, append to existing images; otherwise replace
        const visibleCurrent = currentImages.filter(img => !removedImageIds.includes(img.id));
        if (visibleCurrent.length + uploadedUrls.length <= 3) {
          // Append to existing
          setCurrentImages([...visibleCurrent, ...newCurrentImages]);
        } else {
          // Replace if we're at limit
          setCurrentImages(newCurrentImages);
        }
        
        setExtractedImages([]); // Clear extracted candidates
        setRemovedImageIds([]); // Clear removed images
        setHasCustomUploaded(true); // Mark that custom images were uploaded
        
        showToast(`Successfully uploaded ${uploadedUrls.length} custom image(s). Click "Confirm Images" to save to S3.`, 'success');
        
        // Update product data temporarily (will be saved on Confirm)
        onChange?.('image_urls', [...visibleCurrent.map(img => img.url), ...uploadedUrls].slice(0, 3));
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Failed to upload images: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setUploadLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveCurrentImage = (imageId) => {
    setRemovedImageIds(prev => [...prev, imageId]);
    showToast('Image marked for removal. Click "Confirm Images" to delete it from the product.', 'info');
  };

  const handleRemoveExtractedImage = (imageId) => {
    setExtractedImages(prev => prev.filter(img => img.id !== imageId));
    showToast('Extracted image removed from selection.', 'info');
  };

  const handleToggleExtractedImage = (imageId) => {
    setExtractedImages(prev => prev.map(img => {
      if (img.id === imageId) {
        const newSelected = !img.selected;
        
        // Check max 3 images limit
        const currentSelectedCount = currentImages.filter(img => !removedImageIds.includes(img.id)).length;
        const extractedSelectedCount = prev.filter(img => img.selected).length;
        const totalSelected = currentSelectedCount + extractedSelectedCount + (newSelected ? 1 : -1);
        
        if (newSelected && totalSelected > 3) {
          showToast('Maximum 3 images allowed. Please remove some images first.', 'warning');
          return img; // Don't change selection
        }
        
        return { ...img, selected: newSelected };
      }
      return img;
    }));
  };

  const handleConfirmImages = async () => {
    try {
      // Get images to keep (current images not marked for removal)
      const imagesToKeep = currentImages.filter(img => !removedImageIds.includes(img.id));
      
      // Get selected extracted images
      const selectedExtracted = extractedImages.filter(img => img.selected);
      
      console.log('🔍 Debug Confirm Images - Step 1:', {
        imagesToKeepCount: imagesToKeep.length,
        imagesToKeepUrls: imagesToKeep.map(img => img.url),
        selectedExtractedCount: selectedExtracted.length,
        selectedExtractedUrls: selectedExtracted.map(img => img.url),
        allExtractedImages: extractedImages.map(img => ({ id: img.id, selected: img.selected, url: img.url }))
      });
      
      // Extracted images are already uploaded to staged S3, just get their URLs
      const extractedUrls = selectedExtracted.map(img => img.url);
      
      console.log('📤 Extracted URLs (already in staged S3):', extractedUrls);
      
      // Combine current images with extracted images
      const finalImages = [...imagesToKeep, ...extractedUrls.map(url => ({
        id: `extracted-${Date.now()}-${Math.random()}`,
        url: url,
        title: 'Extracted Image',
        source: 'extracted'
      }))];
      
      console.log('🎯 Final images to save:', {
        finalImagesCount: finalImages.length,
        finalImageUrls: finalImages.map(img => img.url),
        breakdown: {
          currentImagesKept: imagesToKeep.length,
          extractedImagesSelected: extractedUrls.length
        }
      });
      
      // Allow saving with no images - removed warning and early return
      // Extract URLs for API (can be empty array)
      const imageUrls = finalImages.map(img => img.url);
      
      console.log('🌐 Calling API with URLs:', imageUrls);
      
      // Call API to update product images
      const response = await selectRegularProductImages(product.id, imageUrls);
      
      console.log('🌐 API Response:', response);
      
      if (response.selected_images) {
        // Update product data
        onChange?.('image_urls', response.selected_images);
        
        // Update current images (these become the new current set)
        const newCurrentImages = response.selected_images.map((url, index) => ({
          id: `confirmed-${Date.now()}-${index}`,
          url,
          title: `Product Image ${index + 1}`,
          source: 'confirmed'
        }));
        
        setCurrentImages(newCurrentImages);
        setExtractedImages([]);
        setRemovedImageIds([]);
        setHasCustomUploaded(false); // Reset custom upload flag
        
        const deletedCount = removedImageIds.length;
        const extractedCount = extractedUrls.length;
        
        // Show appropriate success message
        if (finalImages.length === 0) {
          showToast('All images removed. Product will be saved without images.', 'success');
        } else {
          showToast(`Images confirmed and saved to S3: ${finalImages.length} kept${deletedCount > 0 ? `, ${deletedCount} deleted` : ''}${extractedCount > 0 ? `, ${extractedCount} extracted` : ''}`, 'success');
        }
      }
    } catch (error) {
      console.error('Failed to confirm images:', error);
      showToast('Failed to confirm images: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Debug: Log product data to check if fields exist
  console.log('ProductEditCard - Product data:', {
    id: product?.id,
    sku: product?.sku,
    sub_part_number: product?.sub_part_number,
    sub_2_part_number: product?.sub_2_part_number,
  });

  // Organized in strict 2-column layout - each row has exactly 2 fields
  const fieldRows = useMemo(() => [
    // Row 1: Description (full width at top)
    [
      { label: 'Description', key: 'description', xs: 12, type: 'textarea' },
    ],
    // Row 2: MFR (Brand) | Category
    [
      { label: 'MFR (Brand)', key: 'brand', xs: 6, type: 'select-manufacturer' },
      { label: 'Category', key: 'category', xs: 6, type: 'select-category' },
    ],
    // Row 3: Condition | Part # (SKU)
    [
      { label: 'Condition', key: 'condition', xs: 6, type: 'select-condition' },
      { label: 'Part # (SKU)', key: 'sku', xs: 6 },
    ],
    // Row 4: Vendor | Location
    [
      { label: 'Vendor', key: 'vendor', xs: 6, type: 'select-vendor' },
      { label: 'Location', key: 'location', xs: 6 },
    ],
    // Row 5: Weight | Dimensions
    [
      { label: 'Weight', key: 'weight', xs: 6, type: 'number-float' },
      { label: 'Dimensions', key: 'dimensions', xs: 6 },
    ],
    // Row 6: Qty (stock_quantity) | Product URL
    [
      { label: 'Qty (stock_quantity)', key: 'stock_quantity', xs: 6, type: 'number-int' },
      { label: 'Product URL', key: 'product_url', xs: 6 },
    ],
    // Row 7: Cost Price | Retail/Current Price
    [
      { label: 'Cost Price', key: 'cost_price', xs: 6, type: 'number-float' },
      { label: 'Retail/Current Price', key: 'retail_price', xs: 6, type: 'number-float' },
    ],
    // Row 8: AI Low : $ | AI High : $
    [
      { label: 'AI Low : $', key: 'ai_low', xs: 6, type: 'number-float' },
      { label: 'AI High : $', key: 'ai_high', xs: 6, type: 'number-float' },
    ],
    // Row 9: Sub P/N | Sub 2 P/N
    [
      { label: 'Sub P/N', key: 'sub_part_number', xs: 6, isCustom: true },
      { label: 'Sub 2 P/N', key: 'sub_2_part_number', xs: 6, isCustom: true },
    ],
    // Row 10: Concatenate Code (full width)
    [
      { label: 'Concatenate Code', key: 'concatenate_code', xs: 12, type: 'textarea' },
    ],
    // Row 11: Specifications (full width)
    [
      { label: 'Specifications (Long Description)', key: 'specifications', xs: 12, type: 'textarea' },
    ],
  ], []);

  const codeRow = useMemo(() => [
    { label: 'Concatenate Code', key: 'concatenate_code', xs: 12, type: 'textarea' },
  ], []);

  const specificationsRow = useMemo(() => [
    { label: 'Specifications (Long Description)', key: 'specifications', xs: 12, type: 'textarea' },
  ], []);

  const renderField = (field) => {
    // Handle custom fields (Sub P/N, Sub 2 P/N)
    if (field.isCustom) {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key} 
          data-xs={field.xs}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            label={field.label}
            value={product?.[field.key] ?? ''}
            fullWidth
            size="small"
            onChange={(e) => onChange?.(field.key, e.target.value || null)}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          />
        </Grid>
      );
    }

    const value = product?.[field.key] ?? '';
    const handleChange = (e) => {
      const raw = e.target.value;
      switch (field.type) {
        case 'number-int': onChange?.(field.key, intOrZero(raw)); break;
        case 'number-float': onChange?.(field.key, numOrNull(raw)); break;
        default: onChange?.(field.key, raw);
      }
    };

    if (field.type === 'select-condition') {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key} 
          data-xs={field.xs}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label="Condition"
            fullWidth
            size="small"
            value={String(value || '')}
            onChange={(e) => onChange?.('condition', e.target.value || null)}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {CONDITION_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'select-category') {
      // Find current category ID based on category name
      const currentCategory = categories.find(cat => cat.name === value);
      const currentCategoryId = currentCategory ? currentCategory.id : '';
      
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key} 
          data-xs={field.xs}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label="Category"
            fullWidth
            size="small"
            value={String(currentCategoryId || '')}
            onChange={(e) => {
              const selectedCategory = categories.find(cat => cat.id === Number(e.target.value));
              if (selectedCategory) {
                onChange?.('category', selectedCategory.name);
                onChange?.('category_id', selectedCategory.id);
              } else {
                onChange?.('category', '');
                onChange?.('category_id', null);
              }
            }}
            disabled={loadingCats}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'select-manufacturer') {
      // Find current manufacturer ID based on brand name
      const currentManufacturer = manufacturers.find(mfr => mfr.name === value);
      const currentManufacturerId = currentManufacturer ? currentManufacturer.id : '';
      
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key} 
          data-xs={field.xs}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label="MFR (Brand)"
            fullWidth
            size="small"
            value={String(currentManufacturerId || '')}
            onChange={(e) => {
              const selectedManufacturer = manufacturers.find(mfr => mfr.id === Number(e.target.value));
              if (selectedManufacturer) {
                onChange?.('brand', selectedManufacturer.name);
                onChange?.('manufacturer_id', selectedManufacturer.id);
              } else {
                onChange?.('brand', '');
                onChange?.('manufacturer_id', null);
              }
            }}
            disabled={loadingMfrs}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {manufacturers.map((mfr) => (
              <MenuItem key={mfr.id} value={mfr.id}>
                {mfr.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'select-vendor') {
      // Vendor is stored as string in product table
      const currentVendor = value || '';
      
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key} 
          data-xs={field.xs}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            select
            label="Vendor"
            fullWidth
            size="small"
            value={String(currentVendor || '')}
            onChange={(e) => {
              onChange?.('vendor', e.target.value || null);
            }}
            disabled={loadingVendors}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          >
            <MenuItem value="">(none)</MenuItem>
            {vendors.map((vendor) => (
              <MenuItem key={vendor} value={vendor}>
                {vendor}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === 'textarea') {
      return (
        <Grid 
          item 
          xs={field.xs} 
          key={field.key} 
          data-xs={field.xs}
          sx={{
            width: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            flexBasis: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            maxWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)',
            minWidth: field.xs === 12 ? '100%' : 'calc(50% - 12px)'
          }}
        >
          <TextField
            label={field.label}
            fullWidth
            multiline
            rows={field.key === 'concatenate_code' ? 2 : 4}
            size="small"
            value={String(value || '')}
            onChange={handleChange}
            sx={{ 
              width: '100% !important',
              minWidth: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                width: '100% !important'
              },
              '& .MuiInputBase-root': {
                width: '100% !important'
              }
            }}
          />
        </Grid>
      );
    }

    return (
      <Grid 
        item 
        xs={field.xs} 
        key={field.key}
        data-xs={field.xs}
      >
        <TextField
          label={field.label}
          value={field.type?.startsWith('number') ? (product?.[field.key] ?? '') : value}
          fullWidth
          size="small"
          onChange={handleChange}
          sx={{ 
            width: '100%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
            }
          }}
        />
      </Grid>
    );
  };

  return (
    <StyledCard sx={{ 
      borderRadius: 2,
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '100%',
      '&:hover': { boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)' }
    }}>
      <CardContent sx={{ 
        p: 3,
        '&:last-child': { pb: 3 }
      }}>
        {/* Render all field rows in strict 2-column layout */}
        {fieldRows.map((row, rowIndex) => (
          <Grid 
            container 
            spacing={3} 
            key={`row-${rowIndex}`} 
            sx={{ 
              width: '100%',
              mb: rowIndex < fieldRows.length - 1 ? 2 : 0,
              boxSizing: 'border-box',
              '& > .MuiGrid-item': {
                boxSizing: 'border-box',
                '&[data-xs="6"]': {
                  flexBasis: 'calc(50% - 12px) !important',
                  maxWidth: 'calc(50% - 12px) !important',
                  width: 'calc(50% - 12px) !important',
                  minWidth: 'calc(50% - 12px) !important'
                },
                '&[data-xs="12"]': {
                  flexBasis: '100% !important',
                  maxWidth: '100% !important',
                  width: '100% !important'
                }
              }
            }}
          >
            {row.map(renderField)}
          </Grid>
        ))}

        {/* Action Buttons - ReviewStagedPanel Style */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ 
            padding: 2, 
            border: '1px solid #ddd', 
            borderRadius: 2, 
            backgroundColor: '#f9f9f9',
            display: 'flex',
            gap: 2,
            alignItems: 'center'
          }}>
            <button
              onClick={handleRunAI}
              disabled={aiLoading}
              style={{
                padding: '8px 16px',
                backgroundColor: aiLoading ? '#ccc' : '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: aiLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {aiLoading ? 'Running AI...' : 'Run Akini AI'}
            </button>
            
            <button
              onClick={handleExtractImages}
              disabled={imageLoading}
              style={{
                padding: '8px 16px',
                backgroundColor: imageLoading ? '#ccc' : '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: imageLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {imageLoading ? 'Extracting...' : 'Extract Images'}
            </button>
          </Box>
        </Box>

        {/* Product Images Section */}
        <Box>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" gutterBottom sx={{ 
              fontWeight: 600, 
              color: 'text.primary',
              mb: 3
            }}>
              Product Images
            </Typography>
            
            {/* Current Images (S3) - Green, pre-selected, X to remove */}
            {(() => {
              const visibleImages = currentImages.filter(img => !removedImageIds.includes(img.id));
              console.log('ProductEditCard - Image display debug:', {
                currentImages: currentImages,
                removedImageIds: removedImageIds,
                visibleImages: visibleImages,
                extractedImages: extractedImages
              });
              return visibleImages.length > 0;
            })() && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                  Current Images (Pre-selected):
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {currentImages.filter(img => !removedImageIds.includes(img.id)).map((image) => (
                    <Card 
                      key={image.id} 
                      sx={{ 
                        width: 160, 
                        height: 160, 
                        border: '3px solid #4caf50', // Green border for current images
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="160"
                        image={image.url}
                        alt={image.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      
                      {/* Selection indicator */}
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: '#4caf50',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </div>
                      
                      {/* Cross button for removal */}
                      <div 
                        style={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          backgroundColor: '#f44336',
                          color: 'white',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleRemoveCurrentImage(image.id)}
                      >
                        ×
                      </div>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* Extracted Images (Candidates) - Selectable */}
            {extractedImages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                  Extracted Images (Select the ones you want to keep):
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {extractedImages.map((image) => (
                    <Card 
                      key={image.id} 
                      sx={{ 
                        width: 160, 
                        height: 160, 
                        border: image.selected ? '3px solid #4caf50' : '3px solid #1976d2',
                        cursor: 'pointer',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => handleToggleExtractedImage(image.id)}
                    >
                      <CardMedia
                        component="img"
                        height="160"
                        image={image.url}
                        alt={image.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      
                      {/* Cross button to remove */}
                      <div 
                        style={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          backgroundColor: '#f44336',
                          color: 'white',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveExtractedImage(image.id);
                        }}
                      >
                        ×
                      </div>
                      
                      {/* Selection indicator */}
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: image.selected ? '#4caf50' : '#1976d2',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 'bold'
                      }}>
                        {image.selected ? '✓' : '○'}
                      </div>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* No images state */}
            {currentImages.length === 0 && extractedImages.length === 0 && (
              <Box sx={{ mb: 3, textAlign: 'center', py: 3 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  No images available. Upload custom image or extract images.
                </Typography>
              </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadCustomImage}
                  disabled={uploadLoading}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    fontSize: '0.9rem'
                  }}
                >
                  {uploadLoading ? 'Uploading...' : 'Upload Custom Image'}
                </Button>
              </Box>
              
              {/* Confirm Images button - Show when:
                  1. Custom images uploaded (hasCustomUploaded)
                  2. Images removed (removedImageIds.length > 0)
                  3. Extracted images selected (extractedImages.some(img => img.selected))
              */}
              {(hasCustomUploaded || removedImageIds.length > 0 || extractedImages.some(img => img.selected)) && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleConfirmImages}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    fontSize: '0.9rem'
                  }}
                >
                  Confirm Images
                </Button>
              )}
            </Box>
        </Box>

        {/* Hidden file input for custom image upload */}
        <input
          type="file"
          accept="image/*"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* Save and Delete Buttons - Icon Only */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 1, 
          mt: 3,
          pt: 2,
          borderTop: '1px solid #e0e0e0'
        }}>
          <IconButton
            onClick={() => setApproveOpen(true)}
            color="primary"
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              width: 40,
              height: 40
            }}
          >
            <CheckIcon />
          </IconButton>
          {product?.id && (
            <IconButton
              onClick={() => setDeleteOpen(true)}
              color="error"
              sx={{
                backgroundColor: 'error.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'error.dark',
                },
                width: 40,
                height: 40
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </CardContent>

      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}>
        <DialogTitle>Save changes?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} color="secondary">No</Button>
          <Button
            onClick={() => { onApprove?.(product); setApproveOpen(false); }}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete this product?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} color="secondary">No</Button>
          <Button
            onClick={() => { onDelete?.(product); setDeleteOpen(false); }}
            color="error"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </StyledCard>
  );
};

export default ProductEditCard;
